import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProductProvider, useProduct } from '@workspace/utils-react';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import AppContainerFromCashForecaster from '../../cash-forecaster/src/components/AppContainer';
import { CookieConsentProvider } from '../../cash-forecaster/src/providers/CookieConsentProvider';
import FrontChannelLogout from './FrontChannelLogout';
import type { PidgetSettings } from './helpers/PidgetSettings';
import LoginCallback from './LoginCallback';
import { AuthBridgeProvider } from './providers/AuthBridgeProvider';
import { EvoMfesProviderWrapper } from './providers/EvoMfesProviderWrapper';
import { FeedbackConfigProvider } from '../../cash-forecaster/src/providers/FeedbackConfigProvider';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const isEmptyInstanceId = (instanceId: string | null | undefined): boolean => {
  return !instanceId || instanceId === '' || instanceId === EMPTY_GUID;
};

// Clear stale OIDC storage on initial app load (runs once)
// This ensures we don't use cached tokens from a previous user after logout/re-login
const clearStaleOidcStorageOnLoad = (() => {
  let hasCleared = false;
  return (authority: string, clientId: string) => {
    if (!hasCleared) {
      const oidcKey = `oidc.user:${authority}:${clientId}`;
      sessionStorage.removeItem(oidcKey);
      hasCleared = true;
    }
  };
})();

// Best practice: clean up URL after successful sign-in (removes code, state params)
const onSigninCallback = (_user: unknown): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

// Stable singleton QueryClient created once at module scope so it is never
// recreated on re-render. Recreating it (e.g. on react-oidc-context auth-state
// updates during silent renew) would wipe the query cache and refetch everything.
const queryClient = new QueryClient();

export const AppContainer = (): React.ReactElement => {
  if (window.location.pathname === '/logout') {
    return <FrontChannelLogout />;
  }

  const product = useProduct<PidgetSettings>();

  if (product?.settings?.identityUrl) {
    const effectiveInstanceId = isEmptyInstanceId(product.instanceId)
      ? product.settings.fallbackInstanceId
      : product.instanceId;

    // Clear stale OIDC storage once on app load to handle logout/re-login with different user
    clearStaleOidcStorageOnLoad(
      product.settings.identityUrl,
      product.settings.clientId,
    );

    return (
      <QueryClientProvider client={queryClient}>
        <ProductProvider iframePidget={product}>
          <AuthProvider
            client_id={product.settings.clientId}
            redirect_uri={window.location.origin}
            silent_redirect_uri={`${window.location.origin}/login/callback`}
            scope={product.settings.scope}
            authority={product.settings.identityUrl}
            onSigninCallback={onSigninCallback}
            automaticSilentRenew={true}
            accessTokenExpiringNotificationTimeInSeconds={60}
          >
            <CookieConsentProvider
              policyUrl={product.settings.cookiePolicyUrl ?? ''}
            >
              <AuthBridgeProvider instanceId={effectiveInstanceId}>
                <EvoMfesProviderWrapper instanceId={effectiveInstanceId}>
                  <FeedbackConfigProvider
                    accountToken={product.settings.sentimentAccountToken}
                    apiHost={product.settings.sentimentApiHost}
                    reDisplayAfterDays={product.settings.sentimentReDisplayAfterDays}
                    mfeRegistryBase={product.settings.mfeRegistryBase}
                    mfeVersion={product.settings.sentimentMfeVersion}
                  >
                    <AppContainerFromCashForecaster
                      defaultMode="live"
                      allowDemoMode={product.settings.allowDemoMode ?? false}
                    />
                  </FeedbackConfigProvider>
                </EvoMfesProviderWrapper>
              </AuthBridgeProvider>
            </CookieConsentProvider>
          </AuthProvider>
        </ProductProvider>
      </QueryClientProvider>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login/callback" element={<LoginCallback />} />
      </Routes>
    </BrowserRouter>
  );
};
